import { useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Button,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { GoogleLoginButton } from "@/components/GoogleLoginButton";

export function Home() {
  const { t } = useTranslation();
  const { isAuthenticated, loading, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const features = [
    {
      icon: <VpnKeyIcon sx={{ fontSize: 40, color: "secondary.main" }} />,
      title: t("home.feature1Title"),
      desc: t("home.feature1Desc"),
    },
    {
      icon: <BarChartIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      title: t("home.feature2Title"),
      desc: t("home.feature2Desc"),
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 40, color: "success.main" }} />,
      title: t("home.feature3Title"),
      desc: t("home.feature3Desc"),
    },
  ];

  return (
    <Container maxWidth="lg">
      {/* Hero Section */}
      <Grid
        container
        spacing={6}
        sx={{
          alignItems: "center",
          py: { xs: 4, md: 8 },
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <Chip
            label={t("app.name")}
            color="primary"
            size="small"
            sx={{ mb: 2 }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              lineHeight: 1.1,
            }}
          >
            {t("home.hero")}{" "}
            <Box component="span" sx={{ color: "primary.main" }}>
              {t("home.heroHighlight")}
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 4,
              maxWidth: 500,
              lineHeight: 1.7,
            }}
          >
            {t("home.heroSubtitle")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              color="secondary"
              sx={{ px: 4 }}
              onClick={loginWithGoogle}
              disabled={loading}
            >
              {t("home.cta")}
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/preview"
              sx={{ px: 4 }}
            >
              {t("preview.cta")}
            </Button>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              maxWidth: 420,
              mx: "auto",
              p: 1,
              bgcolor: "background.paper",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t("home.welcomeTitle")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                {t("home.welcomeSubtitle")}
              </Typography>
              <GoogleLoginButton />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Features Section */}
      <Box sx={{ py: { xs: 4, md: 8 }, textAlign: "center" }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
        >
          {t("home.featuresTitle")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 6,
            maxWidth: 600,
            mx: "auto",
          }}
        >
          {t("home.featuresSubtitle")}
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, idx) => (
            <Grid size={{ xs: 12, md: 4 }} key={idx}>
              <Card sx={{ height: "100%", textAlign: "left" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {feature.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
}
